<template>
  <div class="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
    <!-- Header Section -->
    <div class="relative overflow-hidden py-20 px-4" style="background: linear-gradient(to right, #161542, #374bc6);">
      <div class="absolute inset-0 bg-black opacity-10"></div>
      <div class="relative z-10 max-w-7xl mx-auto text-center">
        <h1 class="text-5xl md:text-7xl font-black text-white mb-4 tracking-tight">
          Gallery
        </h1>
        <p class="text-xl md:text-2xl text-white/90 font-light">
          Capturing Moments, Creating Memories
        </p>
      </div>
      <!-- Decorative elements -->
      <div class="absolute top-10 left-10 w-20 h-20 bg-white/10 rounded-full blur-xl"></div>
      <div class="absolute bottom-10 right-10 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
    </div>

    <!-- Filter Tabs -->
    <div class="max-w-7xl mx-auto px-4 py-12">
      <div class="flex flex-wrap justify-center gap-3 mb-12">
        <button
          v-for="category in categories"
          :key="category"
          @click="selectedCategory = category"
          class="px-6 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105"
          :class="selectedCategory === category 
            ? 'text-white shadow-lg' 
            : 'bg-white text-gray-700 hover:bg-gray-100 shadow-md'"
          :style="selectedCategory === category ? 'background: linear-gradient(to right, #161542, #374bc6);' : ''"
        >
          {{ category }}
        </button>
      </div>

      <!-- Staggered Grid Gallery -->
      <div class="columns-1 md:columns-2 lg:columns-3 xl:columns-4 gap-6 space-y-6">
        <div
          v-for="(image, index) in filteredImages"
          :key="index"
          class="break-inside-avoid group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 cursor-pointer"
          @click="openLightbox(index)"
        >
          <!-- Image -->
          <img
            :src="image.url"
            :alt="image.title"
            class="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-110"
          />
          
          <!-- Overlay -->
          <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div class="absolute bottom-0 left-0 right-0 p-6 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
              <h3 class="text-white text-xl font-bold mb-2">{{ image.title }}</h3>
              <p class="text-white/90 text-sm">{{ image.description }}</p>
              <div class="flex items-center gap-2 mt-3">
                <span class="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white text-xs">
                  {{ image.category }}
                </span>
              </div>
            </div>
          </div>

          <!-- Corner accent -->
          <div class="absolute top-4 right-4 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7"/>
            </svg>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="filteredImages.length === 0" class="text-center py-20">
        <div class="inline-block p-8 bg-gray-100 rounded-full mb-6">
          <svg class="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
          </svg>
        </div>
        <h3 class="text-2xl font-bold text-gray-800 mb-2">No images found</h3>
        <p class="text-gray-600">Try selecting a different category</p>
      </div>
    </div>

    <!-- Lightbox Modal -->
    <transition name="fade">
      <div
        v-if="lightboxOpen"
        class="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
        @click="closeLightbox"
      >
        <button
          @click="closeLightbox"
          class="absolute top-6 right-6 text-white hover:text-gray-300 transition-colors z-50"
        >
          <svg class="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>

        <!-- Navigation -->
        <button
          @click.stop="previousImage"
          class="absolute left-6 text-white hover:text-gray-300 transition-colors"
        >
          <svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
          </svg>
        </button>

        <div class="max-w-6xl w-full" @click.stop>
          <img
            :src="filteredImages[currentImageIndex]?.url"
            :alt="filteredImages[currentImageIndex]?.title"
            class="w-full h-auto max-h-[85vh] object-contain rounded-lg"
          />
          <div class="mt-6 text-center">
            <h3 class="text-white text-2xl font-bold mb-2">
              {{ filteredImages[currentImageIndex]?.title }}
            </h3>
            <p class="text-white/80">
              {{ filteredImages[currentImageIndex]?.description }}
            </p>
          </div>
        </div>

        <button
          @click.stop="nextImage"
          class="absolute right-6 text-white hover:text-gray-300 transition-colors"
        >
          <svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  data() {
    return {
      selectedCategory: 'All',
      categories: ['All', 'Events', 'Coaching', 'Trips', 'Sport'],
      lightboxOpen: false,
      currentImageIndex: 0,
      images: [
        {
          url: require('../assets/images/gallery1.jpg'),
          title: 'Brand Ambassadors 2025',
          description: 'For Mshel Homes',
          category: 'Events'
        },
        {
          url: require('../assets/images/gallery2.jpg'),
          title: 'Executive Portrait',
          description: 'Professional headshot session',
          category: 'Portraits'
        },
        {
          url: require('../assets/images/gallery3.jpg'),
          title: 'Modern Architecture',
          description: 'City skyline at dusk',
          category: 'Architecture'
        },
        {
          url: require('../assets/images/gallery6.jpg'),
          title: 'Forest Path',
          description: 'Nature walk photography',
          category: 'Nature'
        },
        {
          url: require('../assets/images/gallery8.jpg'),
          title: 'Team Building',
          description: 'Office collaboration',
          category: 'Events'
        },
        {
          url: require('../assets/images/wife1.jpg'),
          title: 'Professional Leader',
          description: 'Executive photography',
          category: 'Portraits'
        },
        {
          url: require('../assets/images/prime1.jpg'),
          title: 'Mountain Vista',
          description: 'Panoramic landscape',
          category: 'Nature'
        },
        {
          url: require('../assets/images/prime2.jpg'),
          title: 'Conference Hall',
          description: 'Business meeting space',
          category: 'Events'
        },
        {
          url: require('../assets/images/prime3.jpg'),
          title: 'CEO Portrait',
          description: 'Leadership profile',
          category: 'Portraits'
        },
        {
          url: require('../assets/images/prime4.jpg'),
          title: 'Urban Design',
          description: 'Contemporary building',
          category: 'Architecture'
        },
        {
          url: require('../assets/images/gallery1.jpg'),
          title: 'Abstract Art',
          description: 'Colorful patterns',
          category: 'Abstract'
        },
        {
          url: require('../assets/images/gallery2.jpg'),
          title: 'Sunset Horizon',
          description: 'Golden hour beauty',
          category: 'Nature'
        },
        {
          url: require('../assets/images/gallery3.jpg'),
          title: 'Geometric Shapes',
          description: 'Modern abstract design',
          category: 'Abstract'
        },
        {
          url: require('../assets/images/gallery6.jpg'),
          title: 'Professional Woman',
          description: 'Business portrait',
          category: 'Portraits'
        },
        {
          url: require('../assets/images/gallery8.jpg'),
          title: 'Glass Building',
          description: 'Reflective architecture',
          category: 'Architecture'
        }
      ]
    };
  },
  computed: {
    filteredImages() {
      if (this.selectedCategory === 'All') {
        return this.images;
      }
      return this.images.filter(img => img.category === this.selectedCategory);
    }
  },
  methods: {
    openLightbox(index) {
      this.currentImageIndex = index;
      this.lightboxOpen = true;
      document.body.style.overflow = 'hidden';
    },
    closeLightbox() {
      this.lightboxOpen = false;
      document.body.style.overflow = '';
    },
    nextImage() {
      this.currentImageIndex = (this.currentImageIndex + 1) % this.filteredImages.length;
    },
    previousImage() {
      this.currentImageIndex = (this.currentImageIndex - 1 + this.filteredImages.length) % this.filteredImages.length;
    }
  },
  beforeUnmount() {
    document.body.style.overflow = '';
  }
};
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>